import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { formatDistanceToNow } from 'date-fns';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ui+/ActionButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';

export const Route = createFileRoute('/menu-versions/$menuVersionId')({
  component: Component,
});

function Component() {
  const { menuVersionId } = Route.useParams();

  const convex = useConvex();
  const menuVersionQuery = useQuery(api.menuVersions.compareToLatest, { menuVersionId });

  const handleRestore = async () => {
    try {
      await convex.mutation(api.menuVersions.restore, { menuVersionId });
      toast.success('Menu restored successfully');
    } catch (error) {
      toast.error("Couldn't restore menu", {
        description: getUserErrorMessage(error),
      });
    }
  };

  if (menuVersionQuery.isPending) return <LoadingIcon />;
  if (menuVersionQuery.isError) return <div>Error</div>;

  // Remove some fields from menuVersion and menu that are not relevant for comparison
  const { _creationTime, _id, menuId, ...menuVersionWithoutSystemFields } =
    menuVersionQuery.data.menuVersion;
  const { _creationTime: __, _id: _, ...menuWithoutSystemFields } = menuVersionQuery.data.menu;

  const menuVersionJson = JSON.stringify(menuVersionWithoutSystemFields, null, 2);
  const menuJson = JSON.stringify(menuWithoutSystemFields, null, 2);

  return (
    <div
      className={
        'md:p-4 lg:p-6 menu-versions-diff min-h-[calc(100vh-(--spacing(16)))] bg-muted/60 dark:bg-muted/20'
      }
    >
      {menuJson === menuVersionJson ? (
        <div className="py-4">This menu version is the same as the latest version.</div>
      ) : (
        <>
          <div className="overflow-x-auto text-xs bg-muted border rounded-sm">
            <ReactDiffViewer
              compareMethod={DiffMethod.WORDS}
              hideLineNumbers={true}
              leftTitle={`menuVersion ${menuVersionId}`}
              newValue={menuJson}
              oldValue={menuVersionJson}
              renderContent={(str) => (
                <pre
                  dangerouslySetInnerHTML={{
                    __html: str,
                  }}
                  style={{ lineHeight: 1.0 }}
                />
              )}
              rightTitle={'Latest'}
              showDiffOnly={true} // It's all one line number as far as differ is concerned, so it doesn't matter.
              splitView={true}
              styles={{
                contentText: {
                  fontSize: '11px',
                },
                diffAdded: {
                  padding: '0px',
                },
                // https://www.npmjs.com/package/react-diff-viewer#overriding-styles
                variables: {
                  // When useDarkTheme={true} set below
                  dark: {},
                  light: {
                    codeFoldBackground: '#f7f7f7',
                    codeFoldContentColor: '#666',
                    diffViewerBackground: '#fff',
                    diffViewerColor: 'black',
                    diffViewerTitleBackground: '#eee',
                    diffViewerTitleBorderColor: '#ccc',
                  },
                },
              }}
              useDarkTheme={false}
            />
          </div>
          <div
            className={'mt-1 text-xs text-muted-foreground'}
          >{`Menu version as of ${formatDistanceToNow(menuVersionQuery.data.menuVersion._creationTime, { addSuffix: true })}, ${menuVersionQuery.data.menuVersion._creationTime}`}</div>
          <ActionButton className={'my-4'} onClick={handleRestore} variant="destructive">
            Restore this version
          </ActionButton>
        </>
      )}
    </div>
  );
}
